import { Box, CircularProgress, Collapse } from '@mui/material';
import { TeaserGallery } from '@portals/core/src/containers/TeaserGallery/TeaserGallery';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import {
  CardContentCard,
  CardInfoCard,
  CardMetaCard,
  CardProductCard,
  CardServiceCard,
  ContentCard,
  InfoCard,
  isCardContentCard,
  isCardMetaCard,
  isCardProductCard,
  isCardServiceCard,
  isContentCard,
  isProductCard,
  isServiceCard,
  MetaCard,
  ProductCard,
  ServiceCard,
} from '../../../models';
import { ServiceTeaserGallery as ServiceTeaserGalleryModel } from '../../../models/service-teaser-gallery';
import { CardConsumer } from '../../consumers/CardConsumer/CardConsumer';
import { ContentCardVariants } from '../ContentCard/ContentCard';
import { DividerLink } from '../DividerLink/DividerLink';
import { ProductCardVariants } from '../ProductCard/ProductCard';
import { ServiceCardVariants } from '../ServiceCard/ServiceCard';
import type { Filter } from '../TeaserGalleryFilterBar/TeaserGalleryFilterBar';

const TeaserGalleryFilterBar = dynamic(() =>
  import('../../components/TeaserGalleryFilterBar/TeaserGalleryFilterBar').then((mod) => mod.TeaserGalleryFilterBar)
);

type ContentCardModel = CardContentCard | CardInfoCard | CardMetaCard | CardProductCard | CardServiceCard;
type CardModel = ContentCard | ProductCard | MetaCard | InfoCard | ServiceCard;

type Props = Omit<ServiceTeaserGalleryModel, 'intro'>;

interface ServiceResponse {
  cards: CardModel[];
  count: number;
}

const standardPageSize = 20;
const scrollPageSize = 10;
const maxCardsPerTagForScroll = 50;

interface VariantMapContent {
  [key: string]: ContentCardVariants;
}

const variantMapContent: VariantMapContent = {
  O: 'content:heroThirdRight',
  L: 'content:list',
  'L*': 'content:list',
  '1': 'content:heroHalfRight',
  '2': 'content:oneHalf',
  '3': 'content:oneThird',
  '3*': 'content:oneThird',
  '2I': 'content:oneThird',
  '1l': 'content:heroThirdLeft',
  '1r': 'content:heroThirdRight',
  'R*': 'content:recommendation',
  SS: 'content:small',
};

interface VariantProductContent {
  [key: string]: ProductCardVariants;
}

const variantMapProduct: VariantProductContent = {
  L: 'product:third',
  'L*': 'product:third',
  '2': 'product:half',
  '2S': 'product:halfSquare',
  '3': 'product:third',
  '4': 'product:fourth',
  '3*': 'product:third',
  '1l': 'product:heroThirdLeft',
  '1r': 'product:heroThirdRight',
};

interface VariantServiceContent {
  [key: string]: ServiceCardVariants;
}

const variantMapService: VariantServiceContent = {
  L: 'service:heroMediaLeft',
  'L*': 'service:heroMediaRight',
  LS: 'service:heroMediaSquare',
  '2': 'service:cardHalf',
  '1S': 'service:campaign',
  O: 'service:heroMediaLeft',
  '1': 'service:heroMediaLeft',
  '3*': 'service:list',
  '2I': 'service:heroMediaLeft',
  '1l': 'service:heroMediaLeft',
  '1r': 'service:heroMediaRight',
  R: 'service:heroMediaLeft',
  SS: 'service:heroMediaLeft',
};

const createVariantMap = (splitLayout: string[], cards: ContentCardModel[]) => {
  const variantsColumns = {
    O: 1,
    L: 1,
    'L*': 1,
    '1': 1,
    '2': 2,
    '2S': 2,
    '3': 3,
    '4': 4, // -> sm 2x2
    '3*': 3,
    '2I': 3,
    '1l': 1,
    '1r': 1,
    R: 1,
    'R*': 1,
    SS: 2,
    LS: 1,
    '1S': 1,
  };

  // J = Placeholder when variant starts with a number e.g. (J_2I_3 or J_3)
  // F = Filter
  let currentCard = 0;
  return splitLayout
    .filter((layout: string) => layout !== 'F' && layout !== 'J')
    .reduce((pv, cv) => {
      pv[cv] = [...new Array(variantsColumns[cv])].map(() => {
        const card = cards[currentCard];
        currentCard++;
        if (card && isCardContentCard(card)) {
          return variantMapContent[cv];
        }
        if (card && isCardProductCard(card)) {
          return variantMapProduct[cv];
        }
        if (card && isCardMetaCard(card)) {
          // TODO: Do we need an own variant map?
          return variantMapContent[cv];
        }
        if (card && isCardServiceCard(card)) {
          return variantMapService[cv];
        }
      });

      return pv;
    }, {});
};

const pickListVariantByCard = (
  layout: '3*' | 'L*' | 'R*',
  card: CardModel | ContentCardModel
): ContentCardVariants | ProductCardVariants | ServiceCardVariants => {
  if (('__component' in card && isCardContentCard(card)) || ('__contentType' in card && isContentCard(card))) {
    return variantMapContent[layout];
  }
  if (('__component' in card && isCardProductCard(card)) || ('__contentType' in card && isProductCard(card))) {
    return variantMapProduct[layout];
  }
  if (('__component' in card && isCardServiceCard(card)) || ('__contentType' in card && isServiceCard(card))) {
    return variantMapService[layout];
  }
};

export const ServiceTeaserGallery: React.FC<Props> = ({
  id,
  content,
  tags = [],
  layout,
  tagCounts,
  dividerLink,
  secondaryDividerLinks = [],
}: Props) => {
  const [filters, setFilters] = useState<Filter[]>(
    tags.map(({ key, name }) => {
      return { active: false, key, name, disabled: tagCounts[key] === 0 };
    })
  );
  const splitLayout: string[] = (layout || '').split('_');

  const variantMap = useMemo(() => createVariantMap(splitLayout, content), [splitLayout, content]);
  const [resultCount, setResultCount] = useState<ServiceResponse['count']>(0);
  const [filteredTeasers, setFilteredTeasers] = useState<CardModel[]>([]);
  const [loading, setLoading] = useState(false);

  const [offset, setOffset] = useState(0);

  const endlessLayout = useCallback(
    (card: CardModel) => {
      if (layout?.includes('3*')) {
        return pickListVariantByCard('3*', card);
      }
      if (layout?.includes('R*')) {
        return pickListVariantByCard('R*', card);
      }
      return pickListVariantByCard('L*', card);
    },
    [layout]
  );

  const getListLayout = () => {
    if (layout?.includes('3*')) {
      return '3*';
    }
    if (layout?.includes('R*')) {
      return 'R*';
    }
    return 'L*';
  };

  const cleanseLayoutData = (data: ServiceResponse) => {
    if (layout === 'F_L') {
      let lastTag = null;
      data.cards.forEach((card) => {
        card['image'] = null;
        if (lastTag == card['tags'][0].id) {
          card['tags'] = null;
        } else {
          lastTag = card['tags'][0].id;
        }
      });
    }
    setFilteredTeasers((prevTeasers) => {
      return [...(prevTeasers || []), ...data.cards];
    });
    setResultCount(data.count);
  };

  const outerCollapseReference = useRef<HTMLDivElement>();

  const handleFilterClick = useCallback(
    (filter: Filter) => {
      const newFilters = [...filters];
      newFilters.forEach((item) => (item.active = item === filter ? !item.active : false));
      setFilters(newFilters);

      setFilteredTeasers([]);
      setOffset(0);

      // Scroll to begin of Component
      if (outerCollapseReference.current) {
        outerCollapseReference.current.scrollIntoView({ block: 'start' });
      }
    },
    [filters]
  );

  const activeFilters = useMemo(() => {
    return filters.filter((filter) => filter.active) || [];
  }, [filters]);
  const anyFilterActive = activeFilters.length > 0;

  const maxTeasers = useMemo(
    () => (maxCardsPerTagForScroll + standardPageSize) * activeFilters.length,
    [activeFilters]
  );

  const filteredSecondaryDividerLinks = useMemo(
    () =>
      secondaryDividerLinks.filter((secondaryDividerLink) =>
        activeFilters.some((filter) => filter.key === secondaryDividerLink.tag.key)
      ),
    [secondaryDividerLinks, activeFilters]
  );

  useEffect(() => {
    const dataFetcher = async () => {
      const url = new URL(window.location.origin);
      url.pathname = `/api/service-teaser-galleries/${id}/teasers`;
      url.searchParams.set('offset', `${offset}`);
      url.searchParams.set('limit', `${scrollPageSize}`);
      activeFilters.forEach(({ key }) => url.searchParams.append('tags', key));

      const response = await fetch(url.toString());
      const data = await response.json();
      return cleanseLayoutData(data);
    };

    if (!activeFilters.length) {
      return;
    }

    setLoading(true);
    dataFetcher()
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      })
      .finally(() => setLoading(false));
  }, [activeFilters, offset, id, layout]);

  const increaseOffset = useCallback(() => {
    setOffset((currentOffset) => currentOffset + scrollPageSize);
  }, []);

  const mappedContent = useMemo(() => {
    if (layout === 'F_L') {
      let lastTag = null;

      return content.map((item) => {
        if (isCardContentCard(item)) {
          item['contentCard'].image = null;

          if (item['contentCard'].tags?.length) {
            if (lastTag == item['contentCard']['tags'][0].id) {
              item['contentCard']['tags'] = null;
            } else {
              lastTag = item['contentCard']['tags'][0].id;
            }
          }
        }
        return item;
      });
    }

    return content;
  }, [content, layout]);

  let currentCardIndex = 0;

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: filteredTeasers.length <= maxTeasers && filteredTeasers.length < resultCount,
    onLoadMore: increaseOffset,
  });

  return (
    <>
      <Collapse
        appear={false}
        in={!anyFilterActive}
        ref={outerCollapseReference}
        id={`service-teaser-gallery.${id}.outer`}
      >
        <TeaserGallery id={`service-teaser-gallery.${id}`}>
          {splitLayout.map((layoutElement, index) => {
            if (layoutElement === 'F') {
              return currentCardIndex < mappedContent.length ? (
                <TeaserGalleryFilterBar
                  filters={filters}
                  onFilterClick={handleFilterClick}
                  key={`service-teaser-gallery-filter-bar-${index}`}
                />
              ) : null;
            }
            const cards = [];
            variantMap[layoutElement]?.map((element) => {
              if (currentCardIndex < mappedContent.length && element) {
                cards.push(
                  <CardConsumer
                    node={{ ...mappedContent[currentCardIndex] }}
                    options={{ variant: element }}
                    key={`teaser-teaser-${mappedContent[currentCardIndex].id}`}
                  />
                );
                currentCardIndex++;
              }
            });
            return cards;
          })}
          {mappedContent.slice(currentCardIndex, mappedContent.length).map((element) => {
            const isListLayout = layout?.includes('3*') || layout?.includes('R*') || layout?.includes('L*');
            const listLayout = getListLayout();

            if (isListLayout) {
              return (
                <CardConsumer
                  node={element}
                  options={{ variant: pickListVariantByCard(listLayout, element) }}
                  key={`teaser-teaser-${element.id}`}
                />
              );
            }

            return (
              <CardConsumer
                node={element}
                options={{ variant: pickListVariantByCard('L*', element) }}
                key={`teaser-teaser-${element.id}`}
              />
            );
          })}
        </TeaserGallery>
      </Collapse>
      <Collapse mountOnEnter={true} appear={true} in={anyFilterActive}>
        <TeaserGallery id={`service-teaser-gallery.${id}.list`}>
          <TeaserGalleryFilterBar
            filters={filters}
            onFilterClick={handleFilterClick}
            key={`service-teaser-gallery-filter-bar-${id}`}
          />
          {filteredTeasers?.map((teaser) => (
            <CardConsumer
              node={teaser}
              options={{ variant: endlessLayout(teaser) }}
              key={`teaser-teaser-${teaser.__contentType}-${teaser.id}`}
            />
          ))}
          <Box ref={sentryRef} />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }} key={`service-teaser-gallery-loading-box-${id}`}>
              <CircularProgress />
            </Box>
          ) : null}
        </TeaserGallery>
      </Collapse>
      {!activeFilters.length && <DividerLink dividerLink={dividerLink} />}
      {filteredSecondaryDividerLinks.map((secondaryDividerLink) => (
        <DividerLink
          dividerLink={secondaryDividerLink.link}
          key={`secondary-divider-link-${secondaryDividerLink.id}`}
        />
      ))}
    </>
  );
};
